<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowLeft } from '@lucide/vue'
import { Button, Card, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import WaitlistActionModal from '../components/WaitlistActionModal.vue'
import WaitlistDeliveryTable from '../components/WaitlistDeliveryTable.vue'
import WaitlistStatusBadge from '../components/WaitlistStatusBadge.vue'
import WaitlistTimeline from '../components/WaitlistTimeline.vue'
import { waitlistDetailActions } from '../components/waitlist-actions.js'
import {
  addWaitlistNote,
  getWaitlistEntry,
  inviteWaitlistEntry,
  rejectWaitlistEntry,
  resendWaitlistInvite,
} from '../api/waitlist.js'

const props = defineProps({
  id: { type: String, required: true },
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const loading = ref(false)
const error = ref('')
const detail = ref(null)
const modal = reactive({
  open: false,
  type: 'note',
  loading: false,
  error: '',
})

const entry = computed(() => detail.value.entry)
const events = computed(() => detail.value.events)
const deliveries = computed(() => detail.value.deliveries)
const consentMissing = computed(() => entry.value.marketing_consent_accepted_at === null)

onMounted(() => {
  loadDetail()
})

async function loadDetail() {
  loading.value = true
  error.value = ''
  try {
    detail.value = await getWaitlistEntry(props.id)
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

function openModal(type) {
  modal.open = true
  modal.type = type
  modal.error = ''
}

async function submitModal(input) {
  modal.loading = true
  modal.error = ''
  try {
    if (modal.type === 'invite') await inviteWaitlistEntry(entry.value.id, input)
    if (modal.type === 'resend') await resendWaitlistInvite(entry.value.id, input)
    if (modal.type === 'note') await addWaitlistNote(entry.value.id, input)
    if (modal.type === 'reject') await rejectWaitlistEntry(entry.value.id, input)
    modal.open = false
    toast.success(t('visualGameAdmin.waitlist.toast.saved'))
    await loadDetail()
  } catch (err) {
    modal.error = err.message
    toast.error(t('visualGameAdmin.waitlist.toast.failed'), { duration: 0 })
  } finally {
    modal.loading = false
  }
}

function runAction(action) {
  switch (action) {
    case 'invite':
    case 'resend':
    case 'note':
    case 'reject':
      openModal(action)
      return
    default:
      throw new Error(`Unhandled detail action: ${action}, id: ${entry.value.id}`)
  }
}

function formatOptional(value) {
  return value === null || value === '' ? '-' : value
}
</script>

<template>
  <section class="detail-page vga-page">
    <header class="detail-page__header">
      <Button variant="ghost" @click="router.push({ name: 'waitlist', query: route.query })">
        <ArrowLeft :size="16" />
        {{ t('visualGameAdmin.app.waitlist') }}
      </Button>
      <Button variant="secondary" :loading="loading" @click="loadDetail">
        {{ t('visualGameAdmin.common.retry') }}
      </Button>
    </header>

    <Card v-if="error" data-insp-path="apps/visual-game-admin/src/pages/WaitlistDetail.vue#error">
      <p class="vga-error">{{ error }}</p>
    </Card>

    <Card v-else-if="loading || !detail">
      {{ t('visualGameAdmin.common.loading') }}
    </Card>

    <template v-else>
      <section class="detail-grid">
        <Card data-insp-path="apps/visual-game-admin/src/pages/WaitlistDetail.vue#profile">
          <template #header>{{ t('visualGameAdmin.waitlist.detail.profile') }}</template>
          <dl class="kv">
            <div><dt>{{ t('visualGameAdmin.waitlist.table.email') }}</dt><dd class="breakable">{{ entry.email }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.waitlist.fields.displayName') }}</dt><dd>{{ formatOptional(entry.display_name) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.waitlist.fields.locale') }}</dt><dd>{{ entry.locale }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.waitlist.fields.source') }}</dt><dd>{{ t(`visualGameAdmin.waitlist.source.${entry.source}`) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.waitlist.fields.useCase') }}</dt><dd>{{ entry.use_case }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.waitlist.table.createdAt') }}</dt><dd>{{ entry.created_at }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.waitlist.table.updatedAt') }}</dt><dd>{{ entry.updated_at }}</dd></div>
          </dl>
        </Card>

        <Card data-insp-path="apps/visual-game-admin/src/pages/WaitlistDetail.vue#status">
          <template #header>{{ t('visualGameAdmin.waitlist.detail.status') }}</template>
          <dl class="kv">
            <div>
              <dt>{{ t('visualGameAdmin.waitlist.table.status') }}</dt>
              <dd><WaitlistStatusBadge kind="status" :value="entry.status" :entry-id="entry.id" /></dd>
            </div>
            <div>
              <dt>{{ t('visualGameAdmin.waitlist.table.inviteState') }}</dt>
              <dd><WaitlistStatusBadge kind="invite" :value="entry.invite_state" :entry-id="entry.id" /></dd>
            </div>
            <div><dt>{{ t('visualGameAdmin.waitlist.fields.expiresAt') }}</dt><dd>{{ formatOptional(entry.invite_expires_at) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.waitlist.fields.registeredUserId') }}</dt><dd>{{ formatOptional(entry.registered_user_id) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.waitlist.fields.registeredAt') }}</dt><dd>{{ formatOptional(entry.registered_at) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.waitlist.fields.rejectedReason') }}</dt><dd class="breakable">{{ formatOptional(entry.rejected_reason) }}</dd></div>
          </dl>
        </Card>
      </section>

      <Card data-insp-path="apps/visual-game-admin/src/pages/WaitlistDetail.vue#consent">
        <template #header>{{ t('visualGameAdmin.waitlist.detail.consent') }}</template>
        <p v-if="consentMissing" class="vga-error">{{ t('visualGameAdmin.waitlist.detail.invariantError') }}</p>
        <dl class="kv kv--wide">
          <div><dt>{{ t('visualGameAdmin.waitlist.fields.consentAcceptedAt') }}</dt><dd>{{ formatOptional(entry.marketing_consent_accepted_at) }}</dd></div>
          <div><dt>{{ t('visualGameAdmin.waitlist.fields.consentRevokedAt') }}</dt><dd>{{ formatOptional(entry.marketing_consent_revoked_at) }}</dd></div>
        </dl>
      </Card>

      <Card data-insp-path="apps/visual-game-admin/src/pages/WaitlistDetail.vue#delivery">
        <template #header>{{ t('visualGameAdmin.waitlist.detail.delivery') }}</template>
        <WaitlistDeliveryTable :deliveries="deliveries" />
      </Card>

      <Card data-insp-path="apps/visual-game-admin/src/pages/WaitlistDetail.vue#timeline">
        <template #header>{{ t('visualGameAdmin.waitlist.detail.timeline') }}</template>
        <WaitlistTimeline :events="events" />
      </Card>

      <Card data-insp-path="apps/visual-game-admin/src/pages/WaitlistDetail.vue#notes">
        <template #header>{{ t('visualGameAdmin.waitlist.detail.notes') }}</template>
        <p class="breakable">{{ formatOptional(entry.note) }}</p>
      </Card>

      <Card data-insp-path="apps/visual-game-admin/src/pages/WaitlistDetail.vue#actions">
        <template #header>{{ t('visualGameAdmin.waitlist.detail.actions') }}</template>
        <div class="detail-actions">
          <Button
            v-for="action in waitlistDetailActions(entry)"
            :key="action"
            :variant="action === 'reject' ? 'danger' : 'secondary'"
            @click="runAction(action)"
          >
            {{ t(`visualGameAdmin.common.${action}`) }}
          </Button>
        </div>
      </Card>
    </template>

    <WaitlistActionModal
      v-model:open="modal.open"
      :type="modal.type"
      :loading="modal.loading"
      :error="modal.error"
      @submit="submitModal"
    />
  </section>
</template>

<style scoped>
.detail-page {
  display: grid;
  gap: var(--drama-space-xl);
}

.detail-page__header {
  display: flex;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: var(--drama-space-xl);
}

.kv {
  display: grid;
  gap: var(--drama-space-md);
  margin: 0;
}

.kv--wide {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.kv div {
  display: grid;
  gap: var(--drama-space-xs);
  min-width: 0;
}

.kv dt {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.kv dd {
  margin: 0;
}

.breakable {
  overflow-wrap: anywhere;
}

.detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-md);
}

@media (max-width: 920px) {
  .detail-grid,
  .kv--wide {
    grid-template-columns: 1fr;
  }
}
</style>
